import React, { useState, useRef } from 'react';
import type { FieldArrayRenderProps } from 'react-final-form-arrays';
import { useClickOutside } from '../../../hooks/useClickOutside';
import CheckboxField from '../CheckboxField/CheckboxField';
import Chip from '../../Chip';
import { ListBoxOption } from '../form.styles';
import { DropdownCheckboxListWrapper, StyledDropdownListBox } from './DropdownCheckboxList.styles';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ButtonV2 from '../../Button/ButtonV2';

export type OptionType = {
  value: string | number;
  label: string | React.ReactNode;
};

export type DropdownCheckboxListProps = FieldArrayRenderProps<string | number, HTMLDivElement> & {
  className?: string;
  label: string;
  includeCountInLabel?: boolean;
  options: OptionType[];
  id: string;
  /** callback when chip clicked */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** bottom cta to be rendered on full screen mobile list box  */
  mobileCtaProps?: {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    label: string;
  };
  /** displays caret to the right of text */
  withCaret?: boolean;
};

/**
 * Component to be used with react-final-form.
 * Includes Chip component that opens/closes a list box of checkboxes
 * See form.stories.tsx for an example of implementation
 */
const DropdownCheckboxList: React.FC<DropdownCheckboxListProps> = ({
  className,
  options,
  fields,
  id,
  label,
  includeCountInLabel,
  onClick,
  mobileCtaProps,
  withCaret,
}) => {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  useClickOutside({
    ref,
    onClickOutside: () => {
      setExpanded(false);
    },
    isOpen: expanded,
    exclusion: chipRef,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    if (e.target.checked) {
      fields.push(value);
    } else {
      fields.remove(fields.value.indexOf(value));
    }
  };

  const toggleDropdown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setExpanded(!expanded);
    if (onClick) {
      onClick(e);
    }
  };

  const formatLabel = () => {
    return `${label}${
      includeCountInLabel && fields?.value?.length > 0 ? ` (${fields.value.length})` : ''
    }`;
  };
  const handleMobileCtaClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setExpanded(false);
    if (mobileCtaProps?.onClick) {
      mobileCtaProps.onClick(e);
    }
  };

  return (
    <DropdownCheckboxListWrapper className={className}>
      <Chip
        active={expanded}
        selected={expanded || fields?.value?.length > 0}
        onClick={toggleDropdown}
        htmlFor={id}
        disabled={(options || []).length < 1}
        tabIndex={0}
        ref={chipRef}
        withCaret={withCaret}
      >
        {formatLabel()}
      </Chip>
      {expanded && (
        <StyledDropdownListBox
          ref={ref}
          tabIndex={0}
          onClose={() => setExpanded(false)}
          mobileHeader={label}
          mobileCtaContent={
            mobileCtaProps && (
              <ButtonV2 fullWidth onClick={handleMobileCtaClick}>
                {mobileCtaProps?.label}
              </ButtonV2>
            )
          }
        >
          {options?.map((option, i) => {
            const { value, label: checkboxLabel, ...rest } = option;
            return (
              <ListBoxOption key={`${id}-${i}`}>
                <CheckboxField
                  id={`${id}-${i}`}
                  label={checkboxLabel}
                  checked={fields?.value?.includes(value)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, value)}
                  {...rest}
                />
              </ListBoxOption>
            );
          })}
        </StyledDropdownListBox>
      )}
    </DropdownCheckboxListWrapper>
  );
};

export default DropdownCheckboxList;
